import type { MenuPermission } from '@btsis/core';
import {
  FINANCE_GUIDE_PATH,
  FINANCE_GUIDE_PERMISSIONS,
} from '@/features/finance-guide/finance-guide-access';

export type NavLeaf = {
  path: string;
  titleKey: string;
  icon: string;
  /**
   * The leaf's own menu key — held beside the data rule below for a role to
   * show it, and never what opens the screen (docs/decisions/0124).
   */
  menu: MenuPermission;
  permission?: string;
  /**
   * The screen reads an organization's money — statement lines, matches, the
   * ledger — so a `self` session, which holds no organization, is not offered
   * it whatever permissions it carries (docs/decisions/0087).
   */
  organizationScoped?: true;
  /**
   * Offered to a role that holds `permission` **or** a campaign staff
   * assignment in force that derives it — the work an assignment carries
   * (docs/decisions/0090), which the operations screens are.
   */
  derivable?: true;
  /**
   * Offered to a role that holds `permission` **or** to a campaign staff
   * assignment in force that derives this one instead — the screen a role
   * reaches by its own permission and an assignment by the work it derives:
   * *Gelen Medya*, `inboundMedia.list` or `donation.fulfill`
   * (docs/decisions/0093).
   */
  staffPermission?: string;
  /**
   * Offered to a role holding any one of these instead of `permission` — the
   * finance guide, which every finance list's reader shares.
   */
  anyPermission?: readonly string[];
};

/**
 * A second level, as the Minimal kit's vertical nav-section nests one: the
 * parent is a toggle rather than a link, lit while any of its children is, and
 * shown only when the reader is offered at least one of them.
 */
export type NavGroup = {
  titleKey: string;
  icon: string;
  children: readonly NavLeaf[];
};

export type NavItem = NavLeaf | NavGroup;

export type NavReader = {
  can: (permission: string) => boolean;
  staffMay: (permission: string) => boolean;
  readsOrganizations: boolean;
};

/**
 * Whether a leaf is offered, given what the reader holds — the one rule the
 * sidebar applies. A role reaches a leaf by its data permission **and** its
 * menu key; a campaign staff assignment reaches the operations leaves by what
 * it derives alone, because the *Operasyon Sorumlusu* row grants no key
 * (docs/decisions/0124 §4).
 */
export function navLeafOffered(item: NavLeaf, reader: NavReader): boolean {
  if (item.organizationScoped && !reader.readsOrganizations) return false;
  return roleOffers(item, reader) || assignmentOffers(item, reader);
}

function roleOffers(item: NavLeaf, reader: NavReader): boolean {
  if (!reader.can(item.menu)) return false;
  if (item.anyPermission !== undefined) return item.anyPermission.some(reader.can);
  return !item.permission || reader.can(item.permission);
}

function assignmentOffers(item: NavLeaf, reader: NavReader): boolean {
  if (item.derivable && item.permission) return reader.staffMay(item.permission);
  return item.staffPermission !== undefined && reader.staffMay(item.staffPermission);
}

/** Whether a section's campaign entries are offered — the donation lists they open. */
export function campaignEntriesOffered(entries: NavCampaignEntries, reader: NavReader): boolean {
  return reader.can(entries.menu) && reader.can(entries.permission);
}

export const isNavGroup = (item: NavItem): item is NavGroup => 'children' in item;

/** Every link a section offers, its groups' children included. */
export const navLinksOf = (items: readonly NavItem[]): NavLeaf[] =>
  items.flatMap((item) => (isNavGroup(item) ? item.children : [item]));

/**
 * The entries a section lists from `donation.campaigns.navigation`, one per
 * campaign, under one menu key of their own: a donation type is data, so the
 * role tree cannot name each campaign (docs/decisions/0124).
 */
export type NavCampaignEntries = {
  menu: MenuPermission;
  titleKey: string;
  icon: string;
  permission: string;
};

export type NavSection = {
  titleKey: string;
  items: readonly NavItem[];
  /**
   * The section whose entries come from `donation.campaigns.navigation` in addition to
   * the static ones below. A donation type is data, so the menu that leads to
   * it has to be data too — see .claude/rules/campaign-engine.md. The static
   * items stay, and are what renders while the query is in flight or fails.
   */
  campaignEntries?: NavCampaignEntries;
  /**
   * The section whose *Kampanyalar* group lists, beside its static entry, one
   * link per campaign from `donation.operationCampaigns` — the campaigns the
   * reader carries work out for, each with what waits in their reach
   * (docs/decisions/0091). They are offered exactly when the group's static
   * entry is, and so follow its menu key. *PDF Yönetimi* (docs/decisions/0092)
   * and *Gelen Medya* (docs/decisions/0093) are static entries beside them.
   */
  operationsDriven?: boolean;
};

/** The group of the operations section that the campaigns are listed under. */
export const OPERATION_CAMPAIGNS_GROUP = 'nav.operationCampaigns';

export type OfferedNavSection = NavSection & { campaignEntriesShown: boolean };

/**
 * The sidebar a reader is shown: each leaf by `navLeafOffered`, in the order
 * configured; a group with no leaf left is dropped, and so is a section with
 * neither a leaf nor its campaign entries (docs/decisions/0124, criterion 4).
 */
export function offeredNavSections(
  reader: NavReader,
  sections: readonly NavSection[] = NAV_SECTIONS,
): OfferedNavSection[] {
  return sections.flatMap((section) => {
    const items = section.items.flatMap((item): NavItem[] => {
      if (!isNavGroup(item)) return navLeafOffered(item, reader) ? [item] : [];
      const children = item.children.filter((child) => navLeafOffered(child, reader));
      return children.length > 0 ? [{ ...item, children }] : [];
    });
    const campaignEntriesShown =
      section.campaignEntries !== undefined &&
      campaignEntriesOffered(section.campaignEntries, reader);
    return items.length > 0 || campaignEntriesShown
      ? [{ ...section, items, campaignEntriesShown }]
      : [];
  });
}

// Navigation is permission-filtered at render time. A link here is not
// authorization — the API enforces the same permission independently.
export const NAV_SECTIONS: readonly NavSection[] = [
  {
    titleKey: 'nav.dashboard',
    items: [
      {
        path: '/',
        menu: 'menu.dashboard',
        titleKey: 'nav.dashboard',
        icon: 'solar:pie-chart-2-bold-duotone',
      },
      {
        path: '/gallery',
        menu: 'menu.gallery',
        titleKey: 'nav.gallery',
        icon: 'solar:palette-bold-duotone',
      },
    ],
  },
  {
    titleKey: 'nav.operations',
    operationsDriven: true,
    items: [
      {
        titleKey: OPERATION_CAMPAIGNS_GROUP,
        icon: 'solar:checklist-minimalistic-bold-duotone',
        children: [
          {
            path: '/operations/campaigns',
            menu: 'menu.operationCampaigns',
            titleKey: 'nav.allOperationCampaigns',
            icon: 'solar:checklist-minimalistic-bold-duotone',
            permission: 'donation.fulfill',
            derivable: true,
          },
        ],
      },
      // Every print is a PDF list — docs/decisions/0092. Read by the print's
      // own permission, which a staff assignment derives.
      {
        path: '/operations/printout-lists',
        menu: 'menu.printoutLists',
        titleKey: 'nav.printoutLists',
        icon: 'solar:printer-bold-duotone',
        permission: 'donation.print',
        derivable: true,
      },
      // The evidence that arrives over WhatsApp — docs/decisions/0093. A role
      // reads it by its own permission; a staff assignment reads the matched
      // media of its work, which is fulfilment evidence.
      {
        path: '/operations/inbound-media',
        menu: 'menu.inboundMedia',
        titleKey: 'nav.inboundMedia',
        icon: 'solar:gallery-bold-duotone',
        permission: 'inboundMedia.list',
        staffPermission: 'donation.fulfill',
      },
    ],
  },
  {
    titleKey: 'nav.donations',
    campaignEntries: {
      menu: 'menu.donationCampaigns',
      titleKey: 'nav.donationCampaigns',
      icon: 'solar:folder-bold-duotone',
      permission: 'donation.list',
    },
    items: [
      {
        path: '/donations',
        menu: 'menu.donations',
        titleKey: 'nav.donations',
        icon: 'solar:hand-heart-bold-duotone',
        permission: 'donation.list',
      },
      // Nothing approves a donation, and the waiting list, the order and the
      // fulfilment inbox are the operations pages' since docs/decisions/0091,
      // so none of them is a link of the donation list any more.
      // The trash: deleted donations, kept and put back with one click —
      // docs/decisions/0078. Its own permission, because it shows rows no
      // other screen does.
      {
        path: '/donations/deleted',
        menu: 'menu.deletedDonations',
        titleKey: 'nav.deletedDonations',
        icon: 'solar:trash-bin-trash-bold-duotone',
        permission: 'donation.viewDeleted',
      },
      // A season's own Excel list, entered as staff entry row by row —
      // docs/decisions/0104. Its own permission, never offered alongside
      // *Operasyon İşlemleri*, which is `donation.fulfill`'s surface.
      {
        path: '/donations/imports',
        menu: 'menu.donationImports',
        titleKey: 'nav.donationImports',
        icon: 'solar:import-bold-duotone',
        permission: 'donation.import',
      },
      // The evidence gap (`?gap=1`) and evidence received
      // (`?evidence=received&awaiting=1`) views left the menu after the test
      // round of GitHub issue #2; both stay reachable from the list's own
      // filter menu and by address.
      {
        path: '/donation-boxes',
        menu: 'menu.donationBoxes',
        titleKey: 'nav.donationBoxes',
        icon: 'solar:safe-square-bold-duotone',
        permission: 'donationBox.list',
      },
      {
        path: '/donors',
        menu: 'menu.donors',
        titleKey: 'nav.donors',
        icon: 'solar:users-group-rounded-bold-duotone',
        permission: 'donor.list',
      },
      {
        titleKey: 'nav.campaignManagement',
        icon: 'solar:flag-bold-duotone',
        children: [
          {
            path: '/campaigns',
            menu: 'menu.campaigns',
            titleKey: 'nav.campaigns',
            icon: 'solar:flag-bold-duotone',
            permission: 'campaign.list',
          },
          {
            path: '/campaign-categories',
            menu: 'menu.campaignCategories',
            titleKey: 'nav.campaignCategories',
            icon: 'solar:folder-with-files-bold-duotone',
            permission: 'campaignCategory.list',
          },
          {
            path: '/printout-templates',
            menu: 'menu.printoutTemplates',
            titleKey: 'nav.printoutTemplates',
            icon: 'solar:document-text-bold-duotone',
            permission: 'campaign.view',
          },
          // Which of an institution's own users work a campaign there —
          // docs/decisions/0089. Its own permission, not `campaign.assign`.
          {
            path: '/campaign-staff',
            menu: 'menu.campaignStaff',
            titleKey: 'nav.campaignStaff',
            icon: 'solar:users-group-two-rounded-bold-duotone',
            permission: 'campaignStaff.view',
          },
        ],
      },
      {
        path: '/pledges',
        menu: 'menu.pledges',
        titleKey: 'nav.pledges',
        icon: 'solar:refresh-circle-bold-duotone',
        permission: 'pledge.list',
      },
    ],
  },
  {
    titleKey: 'nav.finance',
    items: [
      // How the finance screens work, for whoever reads any of them.
      {
        path: FINANCE_GUIDE_PATH,
        menu: 'menu.financeGuide',
        titleKey: 'nav.financeGuide',
        icon: 'solar:book-bookmark-bold-duotone',
        anyPermission: FINANCE_GUIDE_PERMISSIONS,
      },
      {
        path: '/statement-imports',
        menu: 'menu.statementImports',
        titleKey: 'nav.statementImports',
        icon: 'solar:upload-square-bold-duotone',
        permission: 'statement.list',
        organizationScoped: true,
      },
      {
        path: '/statement-profiles',
        menu: 'menu.statementProfiles',
        titleKey: 'nav.statementProfiles',
        icon: 'solar:document-text-bold-duotone',
        permission: 'statement.list',
        organizationScoped: true,
      },
      {
        path: '/account-transactions',
        menu: 'menu.accountTransactions',
        titleKey: 'nav.statements',
        icon: 'solar:bill-list-bold-duotone',
        permission: 'statement.list',
        organizationScoped: true,
      },
      {
        path: '/reconciliation',
        menu: 'menu.reconciliation',
        titleKey: 'nav.reconciliation',
        icon: 'solar:transfer-horizontal-bold-duotone',
        permission: 'reconciliation.list',
        organizationScoped: true,
      },
      // The approvals inbox is a saved view of the reconciliation screen, as the
      // donation approvals are of the donation list — §5.5.
      {
        path: '/reconciliation?tab=pending_approval',
        menu: 'menu.reconciliationApprovals',
        titleKey: 'nav.reconciliationApprovals',
        icon: 'solar:check-circle-bold-duotone',
        permission: 'reconciliation.approve',
        organizationScoped: true,
      },
      {
        path: '/reconciliation/shortfalls',
        menu: 'menu.shortfalls',
        titleKey: 'nav.shortfalls',
        icon: 'solar:graph-down-bold-duotone',
        permission: 'reconciliation.list',
        organizationScoped: true,
      },
      {
        path: '/reconciliation/payouts',
        menu: 'menu.payouts',
        titleKey: 'nav.payouts',
        icon: 'solar:card-transfer-bold-duotone',
        permission: 'reconciliation.list',
        organizationScoped: true,
      },
      {
        path: '/refunds',
        menu: 'menu.refunds',
        titleKey: 'nav.refunds',
        icon: 'solar:hand-money-bold-duotone',
        permission: 'refund.list',
        organizationScoped: true,
      },
      {
        path: '/fund-transfers',
        menu: 'menu.fundTransfers',
        titleKey: 'nav.fundTransfers',
        icon: 'solar:transfer-vertical-bold-duotone',
        permission: 'fundTransfer.list',
        organizationScoped: true,
      },
      {
        path: '/finance/institutions',
        menu: 'menu.institutionLedger',
        titleKey: 'nav.institutionLedger',
        icon: 'solar:wallet-money-bold-duotone',
        permission: 'finance.view',
      },
      {
        path: '/finance/balances',
        menu: 'menu.balances',
        titleKey: 'nav.balances',
        icon: 'solar:wallet-money-bold-duotone',
        permission: 'finance.view',
        organizationScoped: true,
      },
      {
        path: '/finance/consolidation',
        menu: 'menu.consolidation',
        titleKey: 'nav.consolidation',
        icon: 'solar:chart-square-bold-duotone',
        permission: 'finance.view',
        organizationScoped: true,
      },
      {
        path: '/receipts',
        menu: 'menu.receipts',
        titleKey: 'nav.receipts',
        icon: 'solar:document-text-bold-duotone',
        permission: 'receipt.list',
      },
      {
        path: '/receipts/annual-certificates',
        menu: 'menu.annualCertificates',
        titleKey: 'nav.annualCertificates',
        icon: 'solar:documents-bold-duotone',
        permission: 'receipt.list',
      },
      {
        path: '/exchange-rates',
        menu: 'menu.exchangeRates',
        titleKey: 'nav.exchangeRates',
        icon: 'solar:dollar-bold-duotone',
        permission: 'exchangeRate.list',
      },
    ],
  },
  {
    titleKey: 'nav.organization',
    items: [
      {
        path: '/regions',
        menu: 'menu.regions',
        titleKey: 'nav.regions',
        icon: 'solar:global-bold-duotone',
        permission: 'orgUnit.list',
      },
      {
        path: '/zones',
        menu: 'menu.zones',
        titleKey: 'nav.zones',
        icon: 'solar:map-bold-duotone',
        permission: 'orgUnit.list',
      },
      {
        path: '/countries',
        menu: 'menu.countries',
        titleKey: 'nav.countries',
        icon: 'solar:flag-bold-duotone',
        permission: 'orgUnit.list',
      },
      {
        path: '/institutions',
        menu: 'menu.institutions',
        titleKey: 'nav.institutions',
        icon: 'solar:buildings-3-bold-duotone',
        permission: 'orgUnit.list',
      },
      {
        path: '/organizations',
        menu: 'menu.organizations',
        titleKey: 'nav.organizations',
        icon: 'solar:case-bold-duotone',
        permission: 'organization.list',
        organizationScoped: true,
      },
    ],
  },
  {
    titleKey: 'nav.system',
    items: [
      {
        path: '/users',
        menu: 'menu.users',
        titleKey: 'nav.users',
        icon: 'solar:user-id-bold-duotone',
        permission: 'user.list',
      },
      {
        path: '/roles',
        menu: 'menu.roles',
        titleKey: 'nav.roles',
        icon: 'solar:shield-user-bold-duotone',
        permission: 'role.list',
      },
      // A person's own generated files. No permission: the requester is the whole
      // authorization — docs/decisions/0043-the-worker.md §7.
      {
        path: '/files',
        menu: 'menu.generatedFiles',
        titleKey: 'nav.generatedFiles',
        icon: 'solar:file-download-bold-duotone',
      },
      // PRD §10's reports — docs/plans/07-notifications-and-reporting.md §7.4.
      {
        path: '/reports',
        menu: 'menu.reports',
        titleKey: 'nav.reports',
        icon: 'solar:chart-square-bold-duotone',
        permission: 'report.view',
      },
      {
        path: '/audit-log',
        menu: 'menu.auditLog',
        titleKey: 'nav.auditLog',
        icon: 'solar:clipboard-list-bold-duotone',
        permission: 'auditLog.list',
      },
      // The worker's operator screens — docs/decisions/0043-the-worker.md §6. Open
      // failures are the ones waiting on a person, so the link lands on them.
      {
        path: '/jobs/failures?status=open',
        menu: 'menu.jobFailures',
        titleKey: 'nav.jobFailures',
        icon: 'solar:danger-triangle-bold-duotone',
        permission: 'jobs.view',
      },
      // docs/decisions/0065: the machines that may write over a signed webhook.
      // Global scope alone, like the operator screens above it.
      {
        path: '/integrations',
        menu: 'menu.integrations',
        titleKey: 'nav.integrations',
        icon: 'solar:plug-circle-bold-duotone',
        permission: 'integration.view',
      },
      {
        path: '/jobs/runs',
        menu: 'menu.scheduledRuns',
        titleKey: 'nav.scheduledRuns',
        icon: 'solar:calendar-bold-duotone',
        permission: 'jobs.view',
      },
      // Who was told what, and whether it arrived — docs/plans/07-notifications-and-reporting.md §7.1.
      {
        path: '/notifications',
        menu: 'menu.notifications',
        titleKey: 'nav.notifications',
        icon: 'solar:bell-bing-bold-duotone',
        permission: 'notification.view',
      },
    ],
  },
];
